import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { fireLessonConfetti } from "../components/Confetti";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Lock, ClipboardList, ArrowRight, Trophy } from "lucide-react";

/**
 * Render **bold** markdown inline without dangerouslySetInnerHTML.
 * Text-only nodes → immune to XSS.
 */
function renderInlineBold(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**") && p.length > 4) {
      return <strong key={i} className="text-ink-900">{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}

export default function Learn() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [cert, setCert] = useState(null);
  const [access, setAccess] = useState(null);
  const [active, setActive] = useState(null); // lesson summary obj
  const [lessonDetail, setLessonDetail] = useState(null);

  useEffect(() => {
    api.get(`/certifications/${slug}`).then(r => {
      setCert(r.data);
      if (r.data.lessons[0]) setActive(r.data.lessons[0]);
    });
    api.get(`/certifications/${slug}/access`).then(r => setAccess(r.data)).catch(()=>{});
  }, [slug]);

  useEffect(() => {
    if (!active) return;
    setLessonDetail(null);
    api.get(`/lessons/${active.id}`).then(r => setLessonDetail(r.data))
      .catch((e) => {
        if (e?.response?.status === 402) {
          toast.error("Cette leçon est verrouillée — veuillez acheter la certification.");
          setLessonDetail({ locked: true });
        }
      });
  }, [active]);

  const completedIds = useMemo(() => new Set(access?.completed_lesson_ids || []), [access]);
  const isCompleted = active && completedIds.has(active.id);
  const allLessonsDone = cert && cert.lessons.every(l => completedIds.has(l.id));

  const markComplete = async () => {
    try {
      await api.post(`/lessons/${active.id}/complete`);
      const msg = fireLessonConfetti();
      toast.success(msg);
      const r = await api.get(`/certifications/${slug}/access`);
      setAccess(r.data);
    } catch { toast.error("Erreur lors de la validation"); }
  };

  if (!cert) return <div className="p-10 text-center text-muted2 font-mono text-sm">Chargement…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="learn-page">
      <Link to={`/certifications/${slug}`} className="text-xs uppercase tracking-widest text-gold-700 font-mono inline-flex items-center gap-1 hover:text-gold-700-700-600">
        <ArrowLeft className="w-3.5 h-3.5" /> Retour à la fiche
      </Link>
      <h1 className="font-display text-3xl md:text-4xl text-ink-900 mt-3">{cert.title}</h1>
      <div className="text-sm text-muted2 mt-1">{cert.lessons.length} leçons · {cert.estimated_duration_hours}h estimées</div>

      <div className="grid lg:grid-cols-[300px,1fr] gap-6 mt-8">
        <aside className="space-y-2 self-start lg:sticky lg:top-24">
          {cert.lessons.map((l) => {
            const locked = !access?.has_purchased && l.display_order > 1;
            const done = completedIds.has(l.id);
            const isActive = active?.id === l.id;
            return (
              <button key={l.id} onClick={() => !locked && setActive(l)}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-all flex items-start gap-3 ${
                  isActive ? "border-gold-400 bg-gold-500/5" : "border-ink-200 hover:border-gold-300"
                } ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
                data-testid={`lesson-nav-${l.display_order}`}>
                <div className={`mt-0.5 w-7 h-7 rounded-md flex items-center justify-center text-xs font-mono ${
                  done ? "bg-success/20 text-success" : "bg-gold-500/10 text-gold-700"
                }`}>
                  {locked ? <Lock className="w-3.5 h-3.5" /> : done ? <CheckCircle2 className="w-4 h-4" /> : l.display_order}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${isActive ? "text-ink-900" : "text-ink-700"}`}>{l.title}</div>
                  <div className="text-[10px] text-muted2 mt-0.5 font-mono">{l.estimated_reading_minutes} min</div>
                </div>
              </button>
            );
          })}

          <div className="gold-divider my-3" />
          <h3 className="text-xs uppercase tracking-widest text-gold-700 font-mono px-1">Évaluation</h3>
          {cert.quizzes.map(q => (
            <button key={q.id} onClick={() => nav(`/quiz/${q.id}`)}
              className="w-full text-left px-4 py-3 rounded-xl border border-ink-200 hover:border-gold-300 transition-all flex items-center gap-3"
              disabled={!access?.has_purchased}
              data-testid={`quiz-nav-${q.quiz_number}`}>
              <ClipboardList className="w-4 h-4 text-gold-700" />
              <div className="flex-1">
                <div className="text-sm text-ink-900">{q.title}</div>
                <div className="text-[10px] text-muted2 font-mono">{q.duration_minutes} min · {q.passing_score_percent}% requis</div>
              </div>
              {!access?.has_purchased && <Lock className="w-3.5 h-3.5 text-muted2" />}
            </button>
          ))}
        </aside>

        <article className="glass rounded-2xl p-6 md:p-10 min-h-[60vh]" data-testid="lesson-content">
          {!lessonDetail && <div className="text-muted2 font-mono text-sm">Chargement de la leçon…</div>}
          {lessonDetail?.locked && (
            <div className="text-center py-20">
              <Lock className="w-10 h-10 mx-auto text-muted2 mb-3" />
              <h3 className="font-display text-2xl text-ink-900">Leçon verrouillée</h3>
              <p className="text-muted2 mt-2 text-sm">Achetez la certification pour accéder à toutes les leçons et au quiz.</p>
              <Link to={`/payment/${slug}`} className="mt-5 inline-block px-6 py-3 rounded-full bg-gold-500 text-ink-900 font-medium hover:bg-gold-500-400">
                Acheter pour {cert.price_euros}€
              </Link>
            </div>
          )}
          {lessonDetail && !lessonDetail.locked && (
            <>
              <div className="text-xs uppercase tracking-widest text-gold-700 font-mono">Module {lessonDetail.display_order} sur {cert.lessons.length}</div>
              <h2 className="font-display text-3xl md:text-4xl text-ink-900 mt-2">{lessonDetail.title}</h2>
              <div className="text-sm text-muted2 mt-1">{lessonDetail.estimated_reading_minutes} min · {lessonDetail.video_key_concept}</div>

              <div className="gold-divider my-6" />

              <div className="prose prose-invert max-w-none">
                {lessonDetail.content.split("\n\n").map((para, i) => {
                  if (para.startsWith("# ")) return <h2 key={`h2-${i}`} className="font-display text-2xl text-ink-900 mt-6 mb-3">{para.slice(2)}</h2>;
                  if (para.startsWith("## ")) return <h3 key={`h3-${i}`} className="font-display text-xl text-gold-700 mt-5 mb-2">{para.slice(3)}</h3>;
                  if (para.startsWith("- ")) return (
                    <ul key={`ul-${i}`} className="list-disc list-inside text-ink-700 my-2 space-y-1">
                      {para.split("\n").map((line, j) => <li key={`ul-${i}-${j}`}>{line.slice(2)}</li>)}
                    </ul>
                  );
                  if (/^\d+\. /.test(para)) return (
                    <ol key={`ol-${i}`} className="list-decimal list-inside text-ink-700 my-2 space-y-1">
                      {para.split("\n").map((line, j) => <li key={`ol-${i}-${j}`}>{line.replace(/^\d+\. /, "")}</li>)}
                    </ol>
                  );
                  // Safe bold rendering — no dangerouslySetInnerHTML, no HTML injection possible
                  return (
                    <p key={`p-${i}`} className="text-ink-700 leading-relaxed my-3">
                      {renderInlineBold(para)}
                    </p>
                  );
                })}
              </div>

              {lessonDetail.key_vocabulary?.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {lessonDetail.key_vocabulary.map(v => (
                    <span key={v} className="text-xs font-mono uppercase tracking-widest px-3 py-1 rounded-full border border-gold-300 text-gold-700">{v}</span>
                  ))}
                </div>
              )}

              <div className="mt-10 flex flex-col sm:flex-row gap-3 items-center justify-between">
                {!isCompleted ? (
                  <button onClick={markComplete} className="px-6 py-3 rounded-full bg-gold-500 text-ink-900 font-medium hover:bg-gold-500-400 shadow-gold inline-flex items-center gap-2" data-testid="mark-lesson-complete">
                    <CheckCircle2 className="w-4 h-4" /> Marquer comme terminée
                  </button>
                ) : (
                  <span className="text-success inline-flex items-center gap-1 text-sm"><CheckCircle2 className="w-4 h-4" /> Leçon validée</span>
                )}
                {allLessonsDone && cert.quizzes[0] && (
                  <Link to={`/quiz/${cert.quizzes[0].id}`} className="inline-flex items-center gap-2 text-gold-700 hover:text-gold-700-700-600 text-sm" data-testid="goto-final-quiz">
                    <Trophy className="w-4 h-4" /> Passer le quiz final <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </>
          )}
        </article>
      </div>
    </div>
  );
}
